import React, { useState, ChangeEvent, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Typography,
  SelectChangeEvent,
  Paper,
  CardContent,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HistoryIcon from "@mui/icons-material/History";
import { HistoricoModal } from "./HistoricoModal";
import { IUsuario, useUsuario } from "../../../Hooks/UsuarioHook";
import { useUsuarioStorage } from "../../../Storage/UsuariosStorage";
import { useNavigate, useParams } from "react-router-dom";
import { UsuarioFoto } from "./UsuarioFoto";
import { Sistemas } from "./Sistemas";
import { WindowRounded } from "@mui/icons-material";
import { PerfilForm } from "./PerfilForm";
import { DadosPessoais } from "./DadosPessoais";
import { Comunicacao } from "./Comunicacao";
import { Localizacao } from "./Localizacao";
import { Senha } from "./Senha";
import { Configuracoes } from "./Configuracoes";
import { podeLiberarSistema } from "../../../Utils/permissoes";
import { Entidade, useEntidade } from "../../../Hooks/EntidadeHook";
import { app_base_url } from "../../../Utils/Urls";
import { RegistroProtocoloEletronico } from "../../../Utils/RegistroDemaisSistema";
import { useSearchParams } from "react-router-dom";

const steps = [
  "Dados Pessoais",
  "Comunicação",
  "Localização",
  "Senha",
  "Configurações",
  "Sistemas",
];

export interface FormData {
  foto: string;
  nome: string;
  tipoPessoa: string;
  cpf: string;
  cnpj: string;
  sexo: string;
  dataNascimento: string;
  descricao: string;
  telefone: string;
  email: string;
  endereco: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  zonaRural: boolean;
  localidadeRural: string;
  administrador: boolean;
  inativo: boolean;
  senha: string;
  confirmarSenha: string;
  servidor: boolean;
  complemento: string;
}

export const Perfil: React.FC = () => {
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [formValid, setFormValid] = useState(false);
  const [openDialog, setOpenDialog] = useState(false); // Estado do modal de confirmação
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get("step"); // Pega o step da query string
  const navigate = useNavigate();
  const { entidadeFocus, setEntidadeFocus } = useEntidade();
  const { carregarUsuariosPorCpfOuCnpj } = useUsuario();
  const [openConfirmModal, setOpenConfirmModal] = useState(false); // Modal de CPF/CNPJ
  const [contaParaPuxar, setContaParaPuxar] = useState<any>(null);
  const [tipoConta, setTipoConta] = useState<"cpf" | "cnpj" | null>(null);
  const [histAberto, setHistAberto] = useState(false); // Modal de histórico (logs)


  const banco = searchParams.get("entidade");
  // Pega o parâmetro "entidade" da URL
  const entidadeBanco = searchParams.get("entidade");



  const confirmCancel = () => {
    //CASO SEJA AUTO CADASTRO TENHO Q RESETAR A ENTIDADE
    setOpenDialog(false);
    if (!entidadeBanco) {
      setEntidadeFocus({} as Entidade)
    }

    // AUTO CADASTRO: fluxo público, precisa voltar para a tela com a entidade
    // na URL — aqui não existe "tela anterior" dentro do app.
    if (entidadeBanco) {
      navigate(`${app_base_url}?entidade=${entidadeBanco}`);
      return;
    }

    // Volta para a tela ANTERIOR (ex.: a lista de Usuários) em vez de jogar na
    // home — quem veio de /usuarios cadastrar alguém continua em /usuarios.
    //
    // Os passos do formulário são estado React (useState), não mudam a rota, então
    // o -1 cai mesmo na tela de origem e não num passo anterior.
    //
    // `history.state.idx` é o índice que o React Router mantém: 0 significa que
    // esta é a PRIMEIRA entrada do histórico (link direto, F5, aba nova). Aí não
    // há para onde voltar e o destino passa a ser a home.
    const indiceNoHistorico = (window.history.state?.idx as number | undefined) ?? 0;

    if (indiceNoHistorico > 0) {
      navigate(-1);
    } else {
      navigate(app_base_url);
    }
  };

  const validateStep = (step: number) => {
    const errors: { [key: string]: string } = {};

    switch (step) {
      case 0: // Valida "Dados Pessoais"
        if (!formData.nome.trim()) errors.nome = "O nome é obrigatório.";
        if (!formData.tipoPessoa)
          errors.tipoPessoa = "Selecione o tipo de pessoa.";
        if (formData.tipoPessoa === "F" && !formData.cpf.trim())
          errors.cpf = "O CPF é obrigatório.";
        if (formData.tipoPessoa === "J" && !formData.cnpj.trim())
          errors.cnpj = "O CNPJ é obrigatório.";
        break;

      case 1: // Valida "Comunicação"
        if (!formData.telefone.trim())
          errors.telefone = "O telefone é obrigatório.";
        if (!formData.email.trim()) errors.email = "O e-mail é obrigatório.";
        break;

      case 2: // Valida "Localização"
        if (!formData.cidade.trim()) errors.cidade = "A cidade é obrigatória.";
        if (!formData.uf.trim()) errors.uf = "O estado (UF) é obrigatório.";
        if (!formData.cep.trim()) errors.cep = "O CEP é obrigatório.";
        if (!formData.zonaRural && !formData.endereco.trim())
          errors.endereco = "O endereço é obrigatório.";
        if (!formData.zonaRural && !formData.bairro.trim())
          errors.bairro = "O bairro é obrigatório.";
        break;

      case 3: // Valida "Senha"
        if (!formData.senha.trim() || formData.senha.length < 4)
          errors.senha = "A senha deve ter no mínimo 4 caracteres.";
        if (formData.senha !== formData.confirmarSenha)
          errors.confirmarSenha = "As senhas não coincidem.";
        break;

      default:
        break;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0; // Retorna true se não houver erros
  };

  const { usuario, token } = useUsuario();
  const isAdmin = usuario?.usuario?.administrador ?? false;

  // A etapa "Sistemas" (6) libera acesso a sistema, e isso é da equipe Sinsoft.
  // Administrador de entidade faz isso pelo modal do card na home, que já o
  // limita aos sistemas dele. Aqui a etapa some da tela por inteiro — mostrar
  // seis caixas cinzas só gera chamado perguntando por que não clica.
  const podeVerEtapaSistemas = isAdmin && podeLiberarSistema(usuario?.usuario);
  const {
    getDetalhesPorUsuario,
    usuarioFocus,
    setUsuarioFocus,
    atualiza_criaUsuario,
  } = useUsuarioStorage();

  const { id_usuario } = useParams();

  useEffect(() => {
    const fetchUsuarioDetails = async () => {
      if (id_usuario) {
        const usuario_ = await getDetalhesPorUsuario(Number(id_usuario));
        setFormData({
          foto: usuario_.usuario.foto,
          nome: usuario_.usuario.nome,
          tipoPessoa: usuario_.usuario.cpf ? "F" : "J",
          cpf: usuario_.usuario.cpf,
          cnpj: usuario_.usuario.cnpj,
          sexo: usuario_.usuario.sexo,
          dataNascimento: usuario_.usuario.data_nascimento
            ? new Date(usuario_.usuario.data_nascimento)
              .toISOString()
              .split("T")[0]
            : "",
          descricao: usuario_.usuario.descricao,
          telefone: usuario_.usuario.telefone,
          email: usuario_.usuario.email,
          endereco: usuario_.usuario.endereco,
          bairro: usuario_.usuario.bairro,
          cidade: usuario_.usuario.cidade,
          uf: usuario_.usuario.uf,
          cep: usuario_.usuario.cep,
          zonaRural: usuario_.usuario.zona_rural,
          localidadeRural: usuario_.usuario.localidade_rural,
          administrador: usuario_.usuario.administrador,
          inativo: !!usuario_.usuario.inativo,
          servidor: usuario_.usuario.servidor,
          senha: "",
          confirmarSenha: "",
          complemento: usuario_.usuario.complemento,
        });
        setUsuarioFocus(usuario_);
      } else if (window.location.href.indexOf("criar-usuario") != -1) {
        const help = {} as IUsuario;
        help.entidade = usuario.entidade;
        setUsuarioFocus(help);
      } else {
        setUsuarioFocus(usuario);
        if (usuario.usuario)
          setFormData({
            foto: usuario.usuario.foto,
            nome: usuario.usuario.nome,
            tipoPessoa: usuario.usuario.cpf ? "F" : "J",
            cpf: usuario.usuario.cpf,
            cnpj: usuario.usuario.cnpj,
            sexo: usuario.usuario.sexo,
            dataNascimento: usuario.usuario.data_nascimento
              ? new Date(usuario.usuario.data_nascimento)
                .toISOString()
                .split("T")[0]
              : "",
            descricao: usuario.usuario.descricao,
            telefone: usuario.usuario.telefone,
            email: usuario.usuario.email,
            endereco: usuario.usuario.endereco,
            bairro: usuario.usuario.bairro,
            cidade: usuario.usuario.cidade,
            uf: usuario.usuario.uf,
            cep: usuario.usuario.cep,
            zonaRural: usuario.usuario.zona_rural,
            localidadeRural: usuario.usuario.localidade_rural,
            administrador: isAdmin,
            inativo: !!usuario.usuario.inativo,
            servidor: usuario.usuario.servidor,
            senha: "",
            confirmarSenha: "",
            complemento: usuario.usuario.complemento,
          });
      }
    };

    fetchUsuarioDetails(); // Chamando a função imediatamente
  }, [usuario, id_usuario]);

  useEffect(() => {
    if (usuarioFocus.usuario) {
      RegistroProtocoloEletronico(usuarioFocus.usuario.id, token);
    }
  }, [usuarioFocus]);

  useEffect(() => {
    if (stepParam) {
      const stepFromParam = parseInt(stepParam, 10);
      if (
        !isNaN(stepFromParam) &&
        stepFromParam >= 0 &&
        stepFromParam < steps.length
      ) {
        setStep(stepFromParam);
      }
    }
  }, [stepParam]);

  const [step, setStep] = useState<number>(0);
  const [formData, setFormData] = useState<FormData>({
    foto: "",
    nome: "",
    tipoPessoa: "F",
    cpf: "",
    cnpj: "",
    sexo: "",
    dataNascimento: "",
    descricao: "",
    telefone: "",
    email: "",
    endereco: "",
    bairro: "",
    cidade: "",
    uf: "RS",
    cep: "",
    zonaRural: false, // Padrão Urbana
    localidadeRural: "",
    administrador: false,
    inativo: false,
    servidor: false,
    senha: "",
    confirmarSenha: "",
    complemento: "",
  });
  const isPasswordValid =
    formData.senha.length >= 4 && formData.senha === formData.confirmarSenha;

  async function salvar() {
    const params = new FormData();

    if (usuarioFocus?.usuario && usuarioFocus?.usuario?.id) {
      params.append("id", String(usuarioFocus?.usuario?.id));
    } else {
      if (usuario?.usuario?.entidade_id) {
        params.append("entidade_id", usuario.usuario.entidade_id.toString());
      } else {
        params.append("entidade_id", String(entidadeFocus.id));
      }
    }

    if (!usuarioFocus?.usuario?.id || step == 0) {
      params.append("cpf", String(formData.cpf));

      params.append("cnpj", String(formData.cnpj));

      params.append("nome", String(formData.nome));

      params.append("descricao", String(formData.descricao));

      params.append("sexo", String(formData.sexo));

      const dataLocal = new Date(formData.dataNascimento + "T00:00:00");
      params.append("data_nascimento", dataLocal.toISOString());

      // params.append("data_nascimento", String(formData.dataNascimento));
    }

    if (!usuarioFocus?.usuario?.id || step == 1) {
      params.append("email", String(formData.email));
      params.append("telefone", String(formData.telefone));
    }

    if (!usuarioFocus?.usuario?.id || step == 2) {
      params.append("endereco", String(formData.endereco));

      params.append("bairro", String(formData.bairro));

      params.append("cidade", String(formData.cidade));

      params.append("uf", String(formData.uf));

      params.append("cep", String(formData.cep));

      params.append("zona_rural", String(formData.zonaRural ? "1" : "0"));

      params.append("localidade_rural", String(formData.localidadeRural));

      params.append("complemento", String(formData.complemento));
    }

    if (!usuarioFocus?.usuario?.id || step == 3) {
      if (formData.senha) {
        params.append("senha", String(formData.senha));
      }
    }

    if (!usuarioFocus?.usuario?.id || step == 4) {
      params.append(
        "administrador",
        String(formData.administrador ? "1" : "0")
      );

      if (formData.inativo) {
        params.append("inativo", String(formData.inativo ? new Date() : null));
      }

      params.append("servidor", String(formData.servidor ? "1" : "0"));
    }

    params.append("usuario", String(""));

    // replica os dados pessoais em todas as contas do mesmo CPF/CNPJ
    // (o backend só replica os campos pessoais; entidade/permissões ficam por conta)
    const data = await atualiza_criaUsuario(params, true);
    if (!usuarioFocus?.usuario?.id) {
      navigate(app_base_url + `/perfil/${data.id}?step=5`);
    }

    /*
    const help_ = {} as IUsuario;
    help_.usuario = {
      foto: formData.foto,
      nome: formData.nome,
      cpf: formData.cpf,
      cnpj: formData.cnpj,
      sexo: formData.sexo,
      data_nascimento: new Date(formData.dataNascimento),
      descricao: formData.descricao,
      telefone: formData.telefone,
      email: formData.email,
      endereco: formData.endereco,
      bairro: formData.bairro,
      cidade: formData.cidade,
      uf: formData.uf,
      cep: formData.cep,
      zona_rural: formData.zonaRural,
      localidade_rural: formData.localidadeRural,
      administrador: formData.administrador,
      inativo: formData.inativo ? new Date() : undefined,
      servidor: formData.servidor,
      senha: formData.senha,
      data_cadastro: usuarioFocus.usuario.data_cadastro,
      entidade_id: usuarioFocus.usuario.entidade_id,
      observacoes: usuarioFocus.usuario.observacoes,
      perfil: usuarioFocus.usuario.perfil,
      id: usuarioFocus.usuario.id,
      ultimo_login: usuarioFocus.usuario.ultimo_login,
      usuario: usuarioFocus.usuario.usuario,
    };
    setUsuarioFocus(help_);
    */
  }

  function renderBotaoProximo() {
    if (step === 3 && !isAdmin) {
      return (
        <Button
          onClick={salvar}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: 500,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
            width: { xs: "100%", sm: "auto" },
          }}
          disabled={formData.senha.trim().length == 0}
        >
          Salvar
        </Button>
      );
    }

    if (step === 4 && isAdmin) {
      return (
        <Button
          onClick={salvar}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: 500,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          Salvar
        </Button>
      );
    }

    return (
      <Button
        onClick={handleNext}
        disabled={step === steps.length - 1}
        variant="contained"
        color="primary"
        sx={{
          borderRadius: 2,
          px: 3,
          fontWeight: 500,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          width: { xs: "100%", sm: "auto" },
        }}
      >
        Próximo {usuarioFocus?.usuario?.id && "(Salvar)"}
      </Button>
    );
  }

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: e.target.hasOwnProperty("checked") ? checked : value,
    }));

    // Resetar campos ao mudar entre Urbana e Rural
    if (name === "zonaRural") {
      setFormData((prev) => ({
        ...prev,
        zonaRural: value === "true",
        endereco: value === "true" ? "" : prev.endereco,
        bairro: value === "true" ? "" : prev.bairro,
        localidadeRural: value === "false" ? "" : prev.localidadeRural,
        complemento: value === "true" ? "" : prev.complemento,
      }));
    }
  };

  const handleNext = () => {

    if (formValid) {
      setStep((prev) => prev + 1);
      if (usuarioFocus?.usuario && usuarioFocus?.usuario?.id) {
        salvar();
      }
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleStepClick = (index: number) => {
    setStep(index);
  };

  useEffect(() => {
    //QUANDO NAO É ADM TENHO Q VOLTAR PARA OS DADOS PESSOAIS
    if ((step === 4 || step === 5) && !isAdmin) {
      setStep(0);
    } else if (step === 5 && !podeVerEtapaSistemas) {
      // a etapa some do stepper, mas ?step=5 na URL ainda chegaria aqui
      setStep(0);
    }
  }, [step]);


  //AUXILIAR USUARIO A CARREGAR OS DADOS JA CADASTRADOS EM OUTRA ENTIDADE
  useEffect(() => {
    if (
      usuario?.usuario?.cpf === formData.cpf ||
      usuario?.usuario?.cnpj === formData.cnpj
    )
      return;

    const buscarContas = async () => {
      let tipo: "cpf" | "cnpj" | null = null;
      let valor: string = "";

      if (formData.cpf.length === 11) {
        tipo = "cpf";
        valor = formData.cpf;
      } else if (formData.cnpj.length === 14) {
        tipo = "cnpj";
        valor = formData.cnpj;
      }

      if (!tipo) return;

      const contas = await carregarUsuariosPorCpfOuCnpj(valor, tipo);

      if (contas.length > 1) {
        const ultima_conta = contas.sort(
          (a, b) =>
            new Date(b.data_cadastro).getTime() -
            new Date(a.data_cadastro).getTime()
        )[0];

        setContaParaPuxar(ultima_conta);
        setTipoConta(tipo);
        if(!usuario?.usuario?.id)//nao quero q abra essa opção caso seja um usuario editando o registro deste usuario ....
        setOpenConfirmModal(true); // 🔹 Abre o modal de confirmação
      }
    };

    buscarContas();
  }, [formData.cpf, formData.cnpj]);

  //funções do modal
  const handleConfirmarPuxar = () => {
    if (!contaParaPuxar || !tipoConta) return;

    const dataFormatada = new Date(contaParaPuxar.data_nascimento)
      .toISOString()
      .split("T")[0];

    setFormData({
      administrador: false,
      bairro: contaParaPuxar.bairro,
      cep: contaParaPuxar.cep,
      cidade: contaParaPuxar.cidade,
      cnpj: tipoConta === "cnpj" ? contaParaPuxar.cnpj : "",
      complemento: contaParaPuxar.complemento,
      confirmarSenha: "",
      cpf: tipoConta === "cpf" ? contaParaPuxar.cpf : "",
      dataNascimento: dataFormatada,
      descricao: contaParaPuxar.descricao,
      email: contaParaPuxar.email,
      endereco: contaParaPuxar.endereco,
      foto: contaParaPuxar.foto,
      inativo: false,
      localidadeRural: contaParaPuxar.localidade_rural,
      nome: contaParaPuxar.nome,
      senha: "",
      servidor: false,
      sexo: contaParaPuxar.sexo,
      telefone: contaParaPuxar.telefone,
      tipoPessoa: tipoConta === "cpf" ? "F" : "J",
      uf: contaParaPuxar.uf,
      zonaRural: contaParaPuxar.zona_rural,
    });

    setOpenConfirmModal(false);
  };

  const handleCancelarPuxar = () => {
    setOpenConfirmModal(false);
  };




  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <DadosPessoais
            formData={formData}
            setFormData={setFormData}
            formValid={formValid}
            setFormValid={setFormValid}
          />
        );

      case 1:
        return (
          <Comunicacao
            formData={formData}
            setFormData={setFormData}
            formValid={formValid}
            setFormValid={setFormValid}
          />
        );

      case 2:
        return (
          <Localizacao
            formData={formData}
            setFormData={setFormData}
            formValid={formValid}
            setFormValid={setFormValid}
          />
        );

      case 3:
        return (
          <Senha
            formData={formData}
            setFormData={setFormData}
            formValid={formValid}
            setFormValid={setFormValid}
          />
        );

      case 4:
        return isAdmin ? (
          <Configuracoes
            formData={formData}
            setFormData={setFormData}
            formValid={formValid}
            setFormValid={setFormValid}
          />
        ) : null;

      case 5:
        return podeVerEtapaSistemas ? <Sistemas /> : null;

      default:
        return <Typography>Erro</Typography>;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: 880,
        margin: "auto",
        p: { xs: 2.5, sm: 4 },
        bgcolor: "background.paper",
        borderRadius: 3,
        border: 1,
        borderColor: "divider",
        boxShadow: (t) =>
          t.palette.mode === "dark"
            ? "0 16px 48px rgba(0,0,0,0.45)"
            : "0 16px 48px rgba(15,23,42,0.08)",
      }}
    >
      {/* Cabeçalho */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            {usuarioFocus?.usuario?.id ? "Editar Perfil" : "Novo Cadastro"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Atualize seus dados — as alterações refletem em todas as suas contas.
          </Typography>
        </Box>

        {usuarioFocus?.usuario?.id && (
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => setHistAberto(true)}
            sx={{ borderRadius: 2, flexShrink: 0, whiteSpace: "nowrap" }}
          >
            Histórico
          </Button>
        )}
      </Box>

      {/* Stepper Responsivo */}
      <Box sx={{ overflowX: "auto", pb: 2 }}>
        <Stepper
          activeStep={step}
          alternativeLabel
          sx={{
            minWidth: "100%",
            flexWrap: "nowrap",
            "& .MuiStepConnector-line": { borderColor: "divider" },
            "& .MuiStepIcon-root": {
              color: "action.disabledBackground",
              fontSize: 30,
            },
            "& .MuiStepIcon-root.Mui-active": { color: "primary.main" },
            "& .MuiStepIcon-root.Mui-completed": { color: "success.main" },
            "& .MuiStepLabel-label": {
              fontSize: { xs: "0.72rem", sm: "0.9rem" },
              mt: { xs: 0.5, sm: 1 },
              "&.Mui-active": { fontWeight: 700, color: "text.primary" },
              "&.Mui-completed": { color: "text.secondary" },
            },
          }}
        >
          {steps
            .filter((_, index) =>
              index === 5
                ? podeVerEtapaSistemas && Boolean(usuarioFocus.usuario)
                : (isAdmin && (usuarioFocus.usuario || index < 5)) || index < 4
            ) // Filtra os passos
            .map((label, index) => (
              <Step
                key={label}
                onClick={() =>
                  usuarioFocus?.usuario?.id ? handleStepClick(index) : () => { }
                }
              >
                <StepLabel sx={{ whiteSpace: "nowrap", cursor: "pointer" }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
        </Stepper>
      </Box>
      {renderStepContent(step)}
      {/* Botões Responsivos */}
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "space-between" },
          flexDirection: { xs: "column", sm: "row" },
          mt: 3,
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          disabled={step === 0}
          onClick={handleBack}
          variant="outlined"
          sx={{ width: { xs: "100%", sm: "auto" } }} // Ajuste aqui!
        >
          Voltar
        </Button>

        <Button
          color="error"
          variant="outlined"
          sx={{ width: { xs: "100%", sm: "auto" } }}
          onClick={() => setOpenDialog(true)} // Abre o modal
        >
          Cancelar
        </Button>

        <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
          {renderBotaoProximo()}
        </Box>
      </Box>
      {/* 🔥 Modal de Confirmação */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>❌ Cancelar Edição</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja cancelar? Todas as alterações serão perdidas.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "space-around" }}>
          <Button
            onClick={() => setOpenDialog(false)}
            color="primary"
            variant="outlined"
          >
            Voltar
          </Button>
          <Button onClick={confirmCancel} color="error" variant="contained">
            Sim, Cancelar
          </Button>
        </DialogActions>
      </Dialog>
      {/* 🔥 Modal de Confirmação CPF/CNPJ */}
      <Dialog open={openConfirmModal} onClose={handleCancelarPuxar}>
        <DialogTitle>Registro encontrado</DialogTitle>
        <DialogContent>
          <Typography>
            Registro de {tipoConta?.toUpperCase()} encontrado em outra entidade.
            Deseja cadastrar com os mesmos dados?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "space-around" }}>
          <Button onClick={handleCancelarPuxar} color="error" variant="outlined">Não</Button>
          <Button onClick={handleConfirmarPuxar} color="primary" variant="contained">Sim</Button>
        </DialogActions>
      </Dialog>

      {/* 🕓 Histórico de auditoria (logs) */}
      {histAberto && usuarioFocus?.usuario?.id && (
        <HistoricoModal
          usuarioId={Number(usuarioFocus.usuario.id)}
          nome={usuarioFocus.usuario.nome}
          onFechar={() => setHistAberto(false)}
        />
      )}

    </Paper>

  );
};